import { useFormik } from 'formik';
import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import clsx from 'clsx';

import HelpLinePopup from '../components/popups/HelpLinePopup';
import { login } from '../service';
import { LoginSchema } from '../utils/schema';
import { getTranslation } from '../utils';
import { Input, Spinner } from '../components/common';
import { StaticHeader } from '../components/partials';
import { ProfileContext } from '../contexts/ProfileContext';

const VALID_LANGUAGES = ['de', 'fr', 'it', 'en'];
const DEFAULT_LANGUAGE = 'de';

export default function SwissLogin() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [profileData, setProfileData] = useContext(ProfileContext);
  const [emailSent, setEmailStatus] = useState(false);
  const [urlParams, setUrlParams] = useState({
    orgPrefix: null,
    id: null,
    aid: null,
    pid: null,
    e: null,
    s: null,
    n: null,
    f: null,
    t: null,
    j: null,
    orgPrefix: null,
    org: null,
    team: null
  });

  useEffect(() => {
    setProfileData({
      ...profileData,
      masterData: { ...profileData.masterData, languages: ['fr', 'en', 'de', 'it'] },
      masterOrganization: '650bd7c379ba3ae92524955a'
    });
  }, []);

  useEffect(() => {
    const selectedLanguage = searchParams.get('lang');
    if (VALID_LANGUAGES.includes(selectedLanguage)) {
      localStorage.setItem('lang', selectedLanguage);
      i18n.changeLanguage(selectedLanguage);
    } else {
      localStorage.setItem('lang', DEFAULT_LANGUAGE);
      i18n.changeLanguage(DEFAULT_LANGUAGE);
    }
  }, []);

  useEffect(() => {
    // Extract URL parameters
    const orgPrefix = searchParams.get('orgPrefix');
    const id = searchParams.get('id');
    const aid = searchParams.get('aid');
    const pid = searchParams.get('pid');
    const e = searchParams.get('e');
    const s = searchParams.get('s');
    const n = searchParams.get('n');
    const t = searchParams.get('t');
    const f = searchParams.get('f');
    const j = searchParams.get('j');
    const org = searchParams.get('org');
    const team = searchParams.get('team');
    const lang = searchParams.get('lang');

    // Store parameters in state
    setUrlParams({
      orgPrefix,
      id,
      aid,
      pid,
      e,
      s,
      n,
      f,
      t,
      j,
      org,
      team,
      lang
    });
  }, [searchParams, i18n]);

  const submitHandler = async (values) => {
    try {
      const response = await login({
        ...values,
        organization: 'find-later',
        ...urlParams,
        lang: i18n.language || urlParams.lang || DEFAULT_LANGUAGE
      });
      if (response?.status === 200) {
        setEmailStatus(true);
      } else {
        toast.error(t('login.error.title'));
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      formik.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema: LoginSchema,
    onSubmit: submitHandler
  });

  const closeModal = () => {
    formik.resetForm();
    setEmailStatus(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <StaticHeader swissHeader={true} />

      {emailSent ? (
        <div className="z-50 backdrop-blur-sm fixed top-0 left-0 w-full h-full flex items-center justify-center">
          <div className="rounded w-[600px] max-w-[100%]">
            <HelpLinePopup
              title={getTranslation(t, 'login.success.title')}
              description={getTranslation(t, 'login.success.description')}
              icon={'email-sent-generic'}
              closeModal={closeModal}
              type="generic"
            />
          </div>
        </div>
      ) : (
        <>
          {/* Scrollable Content */}
          <div className="pt-24 pb-28 min-h-screen overflow-y-auto">
            <div className="px-6 py-16">
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <h1
                    className="text-3xl font-bold mb-6"
                    dangerouslySetInnerHTML={{
                      __html: getTranslation(t, 'swissLoginPage.title')
                    }}
                  />
                  <div
                    className="text-lg text-gray-800 space-y-2"
                    dangerouslySetInnerHTML={{
                      __html: getTranslation(t, 'swissLoginPage.description')
                    }}
                  />
                </div>

                {/* Form section */}
                <div className="my-16">
                  <Input
                    label="E-mail"
                    className="mb-3"
                    labelClassName="text-xl font-normal text-gray-700 mb-2"
                    inputClassName={clsx('text-lg bg-gray-100', {
                      'bg-red-50 border-red-200': formik.errors.email && formik.touched.email
                    })}
                    name="email"
                    placeholder=""
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    error={formik.errors.email}
                    touched={formik.touched.email}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Fixed Footer */}
          <div className="fixed bottom-0 left-0 right-0 w-full bg-white border-t border-gray-300 px-6 py-6 z-50">
            <div className="max-w-2xl mx-auto flex justify-center">
              <button
                onClick={formik.handleSubmit}
                disabled={formik.isSubmitting}
                className="bg-chred-500 hover:bg-chred-400 text-white px-16 py-3 text-lg font-semibold rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                type="button">
                <Spinner isSubmitting={formik.isSubmitting} />
                {getTranslation(t, 'swissLoginPage.confirmButton')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
